import { Box, CircularProgress, Divider, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { PropsWithChildren } from 'react';
import { JiraConfigurationForm } from '../components/JiraConfigurationForm';
import { JiraUrlForm } from './JiraUrlForm';
import { PrimaryButton, TertiaryButton } from '../../../modal-flows/components/Buttons';
import { JiraProjectForm } from './JiraProjectForm';
import { JiraExport } from '../../../integrations/jira/models/JiraExport';
import { JiraSettingsFlowSkeleton } from './skeletons/JiraSettingsFlowSkeleton';
import { t } from 'i18next';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { ACCORDION_STYLES } from '../../../common/components/IntegrationAccordion';

enum Steps {
  JIRA_URL = 1,
  LINK_JIRA_INSTANCE = 2,
  PROJECT_DATA = 3,
}

type Props = {
  initialLoad: boolean;
  isLoading: boolean;
  isJiraExportConfigured: boolean;
  jiraExport: JiraExport | null;
  step: number;
  onCancelClick: () => void;
  onBackClick: () => void;
  onNextClick: (newUrl: string) => void;
  onLinkClick: () => void;
  onSaveClick: (projectKey: string, issueType: string) => void;
  onUnlinkClick: () => void;
  onTabChange: (event: React.SyntheticEvent, newStep: number) => void;
  onTestConnectionClick: (projectKey: string, issueType: string) => void;
};

type TabPanelProps = PropsWithChildren<{ value: number; index: number }>;

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <Box role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`step-${index}`} sx={{ width: '100%', padding: 3 }}>
      {value === index && <Box sx={{ width: '100%', height: '100%' }}>{children}</Box>}
    </Box>
  );
};

const tabA11yProps = (index: number) => {
  return {
    id: `step-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
};

const CircularProgressWithLabel = (props: { value: number; active: number }) => {
  const totalSteps = 3;
  const stepPercent = Math.round((props.value / totalSteps) * 100);
  const progressProps = { value: stepPercent };
  const opacity = props.active === props.value ? '100%' : '20%';
  return (
    <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2, opacity: opacity }}>
      <CircularProgress
        variant="determinate"
        {...progressProps}
        aria-label={t('a11y.jiraIntegrations.stepProgressLabel', { step: props.value, totalSteps: totalSteps })}
      />
      <Typography position="absolute">{props.value}</Typography>
    </Box>
  );
};

export const TAB_STYLES = {
  borderRight: 1,
  borderColor: 'divider',
  width: '50rem',
  alignItems: 'center',
  '& .MuiTabs-indicator': { display: 'none' },
  backgroundImage: ACCORDION_STYLES.backgroundImage,
  pt: 1,
  pb: 1,
};

export const JiraSettingsFlow = ({
  initialLoad,
  isLoading,
  isJiraExportConfigured,
  jiraExport,
  step,
  onCancelClick,
  onBackClick,
  onNextClick,
  onLinkClick,
  onSaveClick,
  onUnlinkClick,
  onTabChange,
  onTestConnectionClick,
}: Props) => {
  const jiraUrl = jiraExport?.getJiraUrl() ?? '';
  const jiraConsumerKey = jiraExport?.getJiraConsumerKey() ?? '';
  const jiraPublicKey = jiraExport?.getJiraPublicKey() ?? '';
  const jiraProjectKey = jiraExport?.getJiraProjectKey() ?? '';
  const jiraIssueTypeKey = jiraExport?.getJiraIssuetypeKey() ?? '';

  const STEP_MAP = {
    [Steps.JIRA_URL]: {
      label: t('jiraIssueExport.tabLabelJiraUrl'),
      value: Steps.JIRA_URL,
      component: () => <JiraUrlForm jiraUrl={jiraUrl} handleClickNext={(newUrl) => onNextClick(newUrl)} />,
    },
    [Steps.LINK_JIRA_INSTANCE]: {
      label: t('jiraIssueExport.tabLabelLinkInstance'),
      value: Steps.LINK_JIRA_INSTANCE,
      component: () => (
        <Stack sx={{ height: '100%', justifyContent: 'space-between' }}>
          <JiraConfigurationForm consumerKey={jiraConsumerKey} publicKey={jiraPublicKey} />
          <Box>
            <Divider textAlign="center" />
            <Stack direction={'row'} sx={{ pt: 1, justifyContent: 'space-between' }}>
              <TertiaryButton onClick={onBackClick}>{t('jiraIssueExport.back')}</TertiaryButton>
              <PrimaryButton onClick={onLinkClick}>{t('jiraIssueExport.link')}</PrimaryButton>
            </Stack>
          </Box>
        </Stack>
      ),
    },
    [Steps.PROJECT_DATA]: {
      label: t('jiraIssueExport.tabLabelFinishConfiguration'),
      value: Steps.PROJECT_DATA,
      component: () => (
        <JiraProjectForm
          jiraBaseUrl={jiraUrl}
          jiraProjectKey={jiraProjectKey}
          jiraIssueType={jiraIssueTypeKey}
          isJiraExportConfigured={isJiraExportConfigured}
          handleClickCancel={onCancelClick}
          handleClickSave={onSaveClick}
          handleClickUnlink={onUnlinkClick}
          handleClickTestConnection={onTestConnectionClick}
        />
      ),
    },
  };

  const isStepActivated = (value: number) => {
    return step < value;
  };

  if (initialLoad) {
    return <JiraSettingsFlowSkeleton />;
  }

  return (
    <ComponentLoader loading={isLoading}>
      <Paper sx={{ display: 'flex', flexGrow: 1 }}>
        <Tabs
          aria-label={t('a11y.jiraIntegrations.jiraSettingsTabsLabel')}
          orientation="vertical"
          variant="scrollable"
          value={step ?? false}
          onChange={(_event: React.SyntheticEvent, newStep: number) => onTabChange(_event, newStep)}
          sx={TAB_STYLES}
        >
          <Tab
            disabled={isStepActivated(1)}
            value={Steps.JIRA_URL}
            icon={<CircularProgressWithLabel value={Steps.JIRA_URL} active={step} />}
            label={STEP_MAP[Steps.JIRA_URL].label}
            {...tabA11yProps(Steps.JIRA_URL)}
          />
          <Tab
            disabled={isStepActivated(2)}
            value={Steps.LINK_JIRA_INSTANCE}
            icon={<CircularProgressWithLabel value={Steps.LINK_JIRA_INSTANCE} active={step} />}
            label={STEP_MAP[Steps.LINK_JIRA_INSTANCE].label}
            {...tabA11yProps(Steps.LINK_JIRA_INSTANCE)}
          />
          <Tab
            disabled={isStepActivated(3)}
            value={Steps.PROJECT_DATA}
            icon={<CircularProgressWithLabel value={Steps.PROJECT_DATA} active={step} />}
            label={STEP_MAP[Steps.PROJECT_DATA].label}
            {...tabA11yProps(Steps.PROJECT_DATA)}
          />
        </Tabs>
        <TabPanel value={step} index={Steps.JIRA_URL}>
          {STEP_MAP[Steps.JIRA_URL].component()}
        </TabPanel>
        <TabPanel value={step} index={Steps.LINK_JIRA_INSTANCE}>
          {STEP_MAP[Steps.LINK_JIRA_INSTANCE].component()}
        </TabPanel>
        <TabPanel value={step} index={Steps.PROJECT_DATA}>
          {STEP_MAP[Steps.PROJECT_DATA].component()}
        </TabPanel>
      </Paper>
    </ComponentLoader>
  );
};
